import { gql } from "@apollo/client";
import { Flex } from "@chakra-ui/react";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { WithRequiredHasuraRoles } from "src/components/Permissions/WithRequiredHasuraRoles";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import { CreateNewFormTemplateButton } from "src/scenes/orgAdmin/formTemplates/components/CreateNewFormTemplateButton";
import { FormTemplatesList } from "src/scenes/orgAdmin/formTemplates/List";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import { useEnrollmentPeriodContext } from "../Edit";

export const GET_FORM_TEMPLATES_FOR_FORM_TEMPLATES_TAB = gql`
  query GetFormTemplatesForFormTemplatesTab(
    $enrollment_period: uuid!
    $include_rules: Boolean = false
  ) {
    form_template(
      where: {
        enrollment_period_id: { _eq: $enrollment_period }
        deleted_at: { _is_null: true }
      }
      order_by: { created_at: asc }
    ) {
      id
      name
      description
      active
      lottery_offers_enabled
      offer_start_at
      locked
      mode
      schema_version
      allow_form_deletion_by_family
      show_waitlist_position
      form_template_rule @include(if: $include_rules) {
        rules {
          status_rule {
            next_form_template {
              name
            }
          }
        }
      }
    }
  }
`;

export const FormTemplatesTab: React.FC = () => {
  const { enrollmentPeriod } = useEnrollmentPeriodContext();
  const isAvelaAdmin = useRequiredHasuraRoles([HasuraRole.ADMIN]);
  const flags = useFlags(["form-builder-draft-mode"]);

  const { remoteData } = useRemoteDataQuery<
    GQL.GetFormTemplatesForFormTemplatesTab,
    GQL.GetFormTemplatesForFormTemplatesTabVariables
  >(GET_FORM_TEMPLATES_FOR_FORM_TEMPLATES_TAB, {
    variables: {
      enrollment_period: enrollmentPeriod.id,
      include_rules: isAvelaAdmin,
    },
    skip: !flags["form-builder-draft-mode"].enabled,
  });

  const { remoteData: formTemplatesWIthoutDraftRD } = useRemoteDataQuery<
    GQL.GetFormTemplatesByEnrollmentPeriodWithoutDraft,
    GQL.GetFormTemplatesByEnrollmentPeriodWithoutDraftVariables
  >(
    gql`
      query GetFormTemplatesByEnrollmentPeriodWithoutDraft(
        $enrollment_period: uuid!
        $include_rules: Boolean = false
      ) {
        form_template(
          where: {
            enrollment_period_id: { _eq: $enrollment_period }
            deleted_at: { _is_null: true }
            mode: { _neq: Draft }
          }
          order_by: { created_at: asc }
        ) {
          id
          schema_version
          name
          description
          active
          lottery_offers_enabled
          offer_start_at
          locked
          mode
          allow_form_deletion_by_family
          show_waitlist_position
          form_template_rule @include(if: $include_rules) {
            rules {
              status_rule {
                next_form_template {
                  name
                }
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        enrollment_period: enrollmentPeriod.id,
        include_rules: isAvelaAdmin,
      },
      fetchPolicy: "network-only",
      skip: flags["form-builder-draft-mode"].enabled,
    }
  );

  if (!flags["form-builder-draft-mode"].enabled) {
    return (
      <GQLRemoteDataView remoteData={formTemplatesWIthoutDraftRD}>
        {(data) => (
          <Flex direction="column">
            <WithRequiredHasuraRoles roles={[HasuraRole.ADMIN]}>
              <Flex alignSelf="flex-end" mb={4}>
                <CreateNewFormTemplateButton />
              </Flex>
            </WithRequiredHasuraRoles>

            <FormTemplatesList formTemplates={data.form_template} />
          </Flex>
        )}
      </GQLRemoteDataView>
    );
  }

  return (
    <GQLRemoteDataView remoteData={remoteData}>
      {(data) => (
        <Flex direction="column">
          <WithRequiredHasuraRoles
            roles={[HasuraRole.ADMIN, HasuraRole.ORG_ADMIN]}
          >
            <Flex alignSelf="flex-end" mb={4}>
              <CreateNewFormTemplateButton />
            </Flex>
          </WithRequiredHasuraRoles>

          <FormTemplatesList formTemplates={data.form_template} />
        </Flex>
      )}
    </GQLRemoteDataView>
  );
};
